import each from 'lodash/each';
import loglevel from 'loglevel';
import { sendRequest } from 'zoomforth_zfutils/xhr_tools.js';
import { usesGibibytes } from 'zoomforth_zfutils/feature_detection.js';

const MAX_UPLOAD_BYTES = 2000000000;


const bytesToGb = (bytes)=> {
  // On windows we use Gibibytes since thats what the file manager reports
  let BYTES_IN_GB;
  try {
    BYTES_IN_GB = usesGibibytes() ?
        1024**3
      :
        1000**3;    
  } catch (e) { 
    // It would be really dumb to fail uploads because of this calculation
    // so we catch here
    BYTES_IN_GB = 1000**3;
  }

  return Math.round(
    bytes / (BYTES_IN_GB) // Calculate GB
    * 100) / 100; // Round to 0.00 decimal
};


const uploadFileToS3 = (file, awsSettings, eventListeners)=> {
  const {
    url,
    fields
  } = awsSettings;
  
  const {
    onUploadProgress,
    onUploadComplete,
    onUploadFailed,
    onUploadCanceled
  } = eventListeners;
  
  const payload = new FormData();

  each(fields, (value, key)=> {
    // The keys in here includes
    // AWSAccessKeyId, acl, file, success_action_status, x-amz-server-side-encryption
    payload.append(key, value);
  });
  payload.append('file', file);
  
  const xhr = new XMLHttpRequest();
  xhr.upload.addEventListener('progress', onUploadProgress, false);
  xhr.addEventListener('load', onUploadComplete, false);
  xhr.addEventListener('error', onUploadFailed, false);
  xhr.addEventListener('abort', onUploadCanceled, false);
  // order still matters again, below:
  xhr.open('POST', url, true);
  xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
  xhr.send(payload);
};


export const uploadAndProcessFile = (file, listeners, extraParams)=> {
  const {
    onPrecursorCreation,
    onUploadProgress,
    onUploadFailure,
    onBeginProcessing,
    onProcessingError,
    onProcessingSuccess
  } = listeners;

  const {
    just_uploaded_from,
    force_asset_type
  } = extraParams;
  
  if (file.size > MAX_UPLOAD_BYTES) {
    const errorMessage = `Unable to upload file larger than ${bytesToGb(MAX_UPLOAD_BYTES)}GB. ` + 
      `Received file of size ${bytesToGb(file.size)}GB.`;
    if (onUploadFailure) {
      onUploadFailure({ error: errorMessage });
    }
    loglevel.error(errorMessage);
    return;
  }

  const precursorCreationParams = {
    name: file.name,
    mime: file.type,
    size: file.size,
    // At the moment we do not, but if we wanted to use this component in prompts, 
    // we would need to allow a dynamic upload_context, and we would need to pass an account_id.
    upload_context: 'zoomforth_user_upload',
    force_asset_type
  };
  sendRequest({
    url: '/asset_precursors',
    params: precursorCreationParams,
    method: 'POST',
    onSuccess: (precursor)=> {
      if (onPrecursorCreation) {
        onPrecursorCreation(precursor);
      }
      uploadFileToS3(
        file, 
        precursor.s3Params, 
        {
          onUploadComplete: (e)=>{
            if (onBeginProcessing) {
              onBeginProcessing(precursor, e);
            }
            sendRequest({
              url: '/asset_precursors/' + precursor.id,
              params: {
                status: 'processing',
                just_uploaded_from
              },
              method: 'PATCH',
              onSuccess: (updatedPrecursor)=> {
                if (updatedPrecursor.error) {
                  onProcessingError(updatedPrecursor);
                } else {
                  onProcessingSuccess(updatedPrecursor);
                }
              }
              // the default xhr error is what we want if the endpoint does not 200
            });
          },
          onUploadProgress: (e)=>{
            if (onUploadProgress) {
              const percentLoaded = parseInt(e.loaded / e.total * 100, 10);
              onUploadProgress(precursor, e, percentLoaded);
            }
          },
          onUploadFailed: (e)=>{
            if (onUploadFailure) {
              onUploadFailure(precursor, e);
            }
          },
          onUploadCanceled: (e)=>{
            if (onUploadFailure) {
              onUploadFailure(precursor, e);
            }
          }
        }
      );
    }
    // no onError is set because it should not happen so the default error is fine
  });
};
